/**
 * WHAT A CALL'S SYSTEM ANSWERS — the three settings of a `call` step that read the owner's own
 * system's answer (P-10.6, P-10.21). PURE: each takes a step and returns a new one.
 *
 *  - `reasonPath`: where the system writes WHY it said no. The reason is sent to the customer
 *    word for word. Blank means the usual places (`message`, `error.message`, …).
 *  - `sayResult`: the system's own message, sent word for word, on the arm the owner picks.
 *  - `answers`: "If your system answers …", one field, at most four cases of at most five
 *    values each (O-17). Anything else goes on as when it works.
 *
 * Only a COMPLETE setting is written to the step: the server refuses an `answers` with no
 * field or no case, and a `sayResult` with no path. The screen keeps what is half-typed in its
 * own state and writes through these.
 */

/** O-17: at most this many cases… */
export const MAX_ANSWER_CASES = 4;
/** …of at most this many values each. */
export const MAX_CASE_VALUES = 5;

const withoutKey = (step, key) => {
  const out = { ...step };
  delete out[key];
  return out;
};

/** The reason path, trimmed; blank removes it. */
export const withReasonPath = (step, path) => {
  const value = String(path ?? '').trim();
  return value === ''
    ? withoutKey(step, 'reasonPath')
    : { ...step, reasonPath: value };
};

/**
 * The system's own message: `{ path, pathAr, on }`, or `null` to stop sending it. A blank path
 * removes it; a blank Arabic path is left out.
 */
export const withSayResult = (step, say) => {
  const path = String(say?.path ?? '').trim();
  if (say === null || path === '') return withoutKey(step, 'sayResult');
  const pathAr = String(say.pathAr ?? '').trim();
  const on = ['worked', 'failed', 'both'].includes(say.on) ? say.on : 'worked';
  return {
    ...step,
    sayResult: { path, ...(pathAr === '' ? {} : { pathAr }), on },
  };
};

/** "a, b , c" as the values a case matches, trimmed, blank ones dropped, at most five. */
export const caseValues = text =>
  String(text ?? '')
    .split(',')
    .map(v => v.trim())
    .filter(v => v !== '')
    .slice(0, MAX_CASE_VALUES);

/**
 * The branch, from what the screen holds: `{ path, cases: [{ values: string, next }] }`. Only
 * the complete cases are written (a value and a step to go to), and nothing at all without a
 * field and one complete case.
 */
export const withAnswers = (step, draft) => {
  const path = String(draft?.path ?? '').trim();
  const cases = (draft?.cases ?? [])
    .map(c => ({ equals: caseValues(c.values), next: String(c.next ?? '') }))
    .filter(c => c.equals.length > 0 && c.next !== '')
    .slice(0, MAX_ANSWER_CASES);
  if (path === '' || cases.length === 0) return withoutKey(step, 'answers');
  return { ...step, answers: { path, cases } };
};

/** What the screen holds for a step's branch, from what is saved on it. */
export const answersDraftOf = step => ({
  path: step?.answers?.path ?? '',
  cases: (step?.answers?.cases ?? []).map(c => ({
    values: (c.equals ?? []).join(', '),
    next: c.next ?? '',
  })),
});
